import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [activeBuilding, setActiveBuildingState] = useState(null);
  const [myBuildings, setMyBuildings] = useState([]);

  // Multi-building helpers
  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json' };
    if (t) headers.Authorization = `Bearer ${t}`;
    if (activeBuilding?.hoa_id) headers['X-Active-Building'] = activeBuilding.hoa_id;
    return headers;
  }, [activeBuilding]);

  const refreshMyBuildings = useCallback(async () => {
    try {
      const t = localStorage.getItem('session_token');
      const h = t ? { Authorization: `Bearer ${t}` } : {};
      const r = await fetch(`${API_URL}/api/me/buildings`, { credentials: 'include', headers: h });
      if (!r.ok) return;
      const d = await r.json();
      setMyBuildings(d.buildings || []);
      const active = (d.buildings || []).find((b) => b.is_active_building)
        || (d.buildings || [])[0]
        || null;
      setActiveBuildingState(active);
    } catch (e) { console.warn('refreshMyBuildings', e); }
  }, []);

  const switchBuilding = useCallback(async (hoa_id) => {
    const t = localStorage.getItem('session_token');
    const h = { 'Content-Type': 'application/json' };
    if (t) h.Authorization = `Bearer ${t}`;
    const r = await fetch(`${API_URL}/api/me/active-building`, {
      method: 'POST',
      credentials: 'include',
      headers: h,
      body: JSON.stringify({ hoa_id }),
    });
    if (!r.ok) return false;
    await refreshMyBuildings();
    return true;
  }, [refreshMyBuildings]);

  const checkAccountStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/account-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const status = await response.json();
        setAccountStatus(status);
        return status;
      }
    } catch (err) {
      console.error('Account status check failed:', err);
    }
    return null;
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Get session token from localStorage as backup
      const storedToken = localStorage.getItem('session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Check account status after successful auth
        await checkAccountStatus();
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [checkAccountStatus]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Whenever user changes (login/logout), refresh their buildings
  useEffect(() => {
    if (user) refreshMyBuildings();
    else { setMyBuildings([]); setActiveBuildingState(null); }
  }, [user, refreshMyBuildings]);

  const loginWithOTP = async (email, opts = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...(opts.mode ? { mode: opts.mode } : {}) }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const err = new Error(data?.detail || 'Failed to send OTP');
        err.status = response.status;
        throw err;
      }
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const verifyOTP = async (email, otp, opts = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp, ...(opts.mode ? { mode: opts.mode } : {}) }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        const err = new Error(data.detail || 'Invalid OTP');
        err.status = response.status;
        throw err;
      }

      // Store session token in localStorage for persistence
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
      }
      
      setUser(data);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange session');
      }

      const userData = await response.json();
      
      // Store session token in localStorage
      if (userData.session_token) {
        localStorage.setItem('session_token', userData.session_token);
      }
      
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('session_token');
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Derive an "effective user" where hoa_id falls back to the active building's hoa_id.
  // This makes every page that reads `user?.hoa_id` work correctly under the multi-building
  // model (where the user record itself has hoa_id=null and access is via user_buildings).
  const effectiveUser = (user && !user.hoa_id && activeBuilding?.hoa_id)
    ? { ...user, hoa_id: activeBuilding.hoa_id }
    : user;

  const value = {
    user: effectiveUser,
    loading,
    error,
    accountStatus,
    activeBuilding,
    myBuildings,
    loginWithOTP,
    verifyOTP,
    loginWithGoogle,
    exchangeSession,
    logout,
    updateProfile,
    checkAuth,
    checkAccountStatus,
    refreshMyBuildings,
    switchBuilding,
    authHeaders,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
